//@flow
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import * as Immutable from 'immutable';
import { LocationId } from 'common/models/internal/LocationId';
import { LineItemPart } from 'common/models/internal/LineItemPart';
import { VolumeUnitType, Volume } from 'common/models/internal/Volume';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import DataUtils from 'common/utils/DataUtils';
import { MasterLineItem } from 'common/models/internal/MasterLineItem';
import { type VolumeModelType } from 'common/models/flow/Volume';
import { Carton } from 'common/models/internal/Carton';
import { Dimensions } from 'common/models/internal/Dimensions';

type SerializeEntityParamsType = { entity: MasterLineItem };

export class LineItemImportHelper extends EntityImportDataHelper {
    static processLocationId({ cellValue, clientId }) {
        return cellValue
            ? [
                  {
                      locationId: cellValue.trim(),
                      clientId
                  }
              ]
            : [];
    }
    static processLocationIds({ cellValue, clientId }) {
        return cellValue
            ? cellValue
                  .split(',')
                  .filter(locationId => locationId?.trim())
                  .map(locationId => {
                      return {
                          locationId: locationId.trim(),
                          clientId
                      };
                  })
            : [];
    }

    static processOtherParts({ cellValue }) {
        const cellValueArray = cellValue?.split(';');

        const processedPart = {};
        cellValue &&
            cellValueArray.forEach(cellValueItem => {
                const [key, value] = cellValueItem.split(':');
                processedPart[key?.trim()] = value?.trim();
            });
        return processedPart;
    }

    static processNotes({ cellValue }) {
        return cellValue ? cellValue.split(';') : [];
    }

    static getProcessedValidationIds(validationIds) {
        return validationIds?.map(id => id.locationId)?.toJS();
    }

    /* Validations */
    static validateSalesCategory({ entitySettings, entity }) {
        const salesCategories =
            entitySettings?.lineItemSettings.getOptionsForSelect() || [];
        return entity.getCategoryValidationMessage({
            salesCategories
        });
    }

    static validateDimensions(key, dimensions) {
        return dimensions.getValidationMessage(key);
    }

    static validatePartDimensions({ entity, header }) {
        const [, key] = header.id.split('.');
        const { parts } = entity;
        const { dimensions } = parts.first();
        return LineItemImportHelper.validateDimensions(key, dimensions);
    }

    static validatePartVolume({ entity }) {
        const { parts } = entity;
        const { volume } = parts.first();
        return volume.getValidationMessage('value');
    }

    static processPartVolume({
        cellValue
    }: {
        cellValue: ?string
    }): ?VolumeModelType {
        if (!DataUtils.isEmpty(cellValue)) {
            return { value: cellValue, unit: VolumeUnitType.MetersCubic };
        }
        return cellValue;
    }

    static serializePartVolume(part: VolumeModelType): Volume {
        return Volume.fromJS(part);
    }

    static validatePartWeight({ entity, header }) {
        const [, key] = header.id.split('.');

        const { parts } = entity;
        return parts.first().getValidationMessage(key);
    }

    static validateOtherPartsDimensions({ entity, header, t }) {
        return LineItemImportHelper.validateOtherPartsProperty({
            entity,
            header,
            t,
            validator: ({ part, key }) => {
                const { dimensions } = part;
                return !!LineItemImportHelper.validateDimensions(
                    key,
                    dimensions
                );
            }
        });
    }

    static validateOtherPartsVolumes({ entity, header, t }) {
        return LineItemImportHelper.validateOtherPartsProperty({
            entity,
            header,
            t,
            validator: ({ part }) => {
                const { volume } = part;
                return volume.getValidationMessage('value');
            }
        });
    }

    static validateOtherPartsWeights({ entity, header, t }) {
        return LineItemImportHelper.validateOtherPartsProperty({
            entity,
            header,
            t,
            validator: ({ part, key }) => {
                return part.getValidationMessage(key);
            }
        });
    }

    static validateOtherPartsProperty({ entity, header, t, validator }) {
        const [, key] = header.id.split('.');
        const { parts } = entity;
        const [, ...otherParts] = parts;

        const hasError = otherParts.some(part => {
            return validator({ part, key });
        });

        return hasError
            ? t(
                  'validations:lineItemValidationErrors.invalidOtherPartsProperty',
                  {
                      key
                  }
              )
            : null;
    }

    static validateCartonDimensions({ entity, header }) {
        const [, key] = header.id.split('.');
        const { carton } = entity;
        return (
            carton?.dimensions &&
            LineItemImportHelper.validateDimensions(key, carton.dimensions)
        );
    }

    static validateCartonVolume({ entity, unitConverter }) {
        const { carton } = entity;
        return carton?.validateVolumeForParts(entity, unitConverter);
    }

    static validateCartonWeight({ entity, unitConverter }) {
        const { carton } = entity;
        return carton?.validateWeightForParts(entity, unitConverter);
    }

    static validateScanId({ entity }) {
        return entity.validateScanId();
    }

    /**
     * This method takes `locations` object and @returns `LocationId` object of `Immutable.List`
     * Used for edit masters table
     * @static
     * @memberof LineItemHelper
     */
    static serializeLocations = (locations: Array<*>): Immutable.List<*> =>
        Immutable.List(locations).map(item => LocationId.fromJS(item));

    static serializeNotes = (notes: Array<string>): Immutable.List<string> =>
        Immutable.List(notes);

    static buildPart = ({ length, breadth, height, volumeValue, weight }) => {
        return LineItemPart.fromJS({
            volume: {
                unit: VolumeUnitType.MetersCubic,
                value: volumeValue
            },
            weight,
            dimensions: {
                length,
                breadth,
                height
            }
        });
    };

    static buildPartObjects = ({
        keys,
        partsLengthObject,
        partsBreadthObject,
        partsHeightObject,
        partsVolumeValueObject,
        partsWeightObject
    }) => {
        const partObjects = [];
        keys.forEach(key => {
            partObjects.push(
                LineItemImportHelper.buildPart({
                    length: partsLengthObject[key],
                    breadth: partsBreadthObject[key],
                    height: partsHeightObject[key],
                    volumeValue: partsVolumeValueObject[key],
                    weight: partsWeightObject[key]
                })
            );
        });
        return partObjects;
    };

    /**
     * Creating formatted part model objects from raw import data fields
     */
    static createPartsFromRawData = otherParts => {
        /**
         * Here, format of length, breadth, height, volumeValue, weight
         * -> { key: Part-<number>, value: <value> }
         */
        const { dimensions, volume, weight: partsWeightObject } = otherParts;
        const {
            length: partsLengthObject,
            breadth: partsBreadthObject,
            height: partsHeightObject
        } = dimensions || {};
        const { value: partsVolumeValueObject } = volume || {};

        //To handle if the parts columns are missing
        const partsToUnion = [];
        if (partsLengthObject) {
            partsToUnion.push(...Object.keys(partsLengthObject));
        }
        if (partsBreadthObject) {
            partsToUnion.push(...Object.keys(partsBreadthObject));
        }
        if (partsHeightObject) {
            partsToUnion.push(...Object.keys(partsHeightObject));
        }
        if (partsVolumeValueObject) {
            partsToUnion.push(...Object.keys(partsVolumeValueObject));
        }
        if (partsWeightObject) {
            partsToUnion.push(...Object.keys(partsWeightObject));
        }

        // Finding number of unique part keys which will help us in creating that many objects
        const uniquePartKeys = new Set(partsToUnion);

        return LineItemImportHelper.buildPartObjects({
            keys: uniquePartKeys,
            partsLengthObject: partsLengthObject || {},
            partsBreadthObject: partsBreadthObject || {},
            partsHeightObject: partsHeightObject || {},
            partsVolumeValueObject: partsVolumeValueObject || {},
            partsWeightObject: partsWeightObject || {}
        });
    };

    /**
     * This method takes part meta and excising row entity and coverts it back to required parts object
     *
     * @param {string} value
     * @param {Object} RowMeta
     */
    static serializeOtherParts = (
        value,
        { cellId, rawValue, entity, ...rest }
    ): Immutable.List<*> => {
        // Updating cell value on flat row
        const row = cellId.row.set(cellId.headerId, rawValue);
        // parsing flat row to model Object
        const otherPartsRaw = EntityImportHelper.makeSingleRowData({
            row,
            ...rest
        })
            .get('otherParts')
            .toJS();
        // Parsing parts object
        const otherParts = LineItemImportHelper.createPartsFromRawData(
            otherPartsRaw
        );
        const allParts = entity
            .get('parts')
            .slice(0, 1) // getting parts which is part 1 in table
            .concat(otherParts); // merging parts and other parts
        return allParts;
    };

    // case for part 1, this always be at 0th index
    static getModelPartAccessor = ({ accessor }) => accessor.splice(1, 0, 0);

    /**
     * LineItemImportHelper.serializeOtherParts method create
       new part object so in other parts case replacing parts object
    **/
    static getModelOtherPartsAccessor = () => Immutable.List(['parts']);

    static processEntityRows = ({ rows }) => {
        return rows.map(entityRow =>
            entityRow.map((cellValue, headerId) => {
                if (headerId === 'otherParts') {
                    const otherParts = cellValue?.toJS();
                    return LineItemImportHelper.createPartsFromRawData(
                        otherParts
                    );
                }
                if (headerId === 'customProperties') {
                    return cellValue.filter(value => value);
                }
                if (headerId === 'carton') {
                    if (
                        cellValue.get('capacity') ||
                        cellValue.getIn(['volume', 'value']) ||
                        cellValue.get('weight') ||
                        cellValue.getIn(['dimensions', 'length']) ||
                        cellValue.getIn(['dimensions', 'breadth']) ||
                        cellValue.getIn(['dimensions', 'height'])
                    ) {
                        return cellValue;
                    }
                    return null;
                }
                return cellValue;
            })
        );
    };

    static getProcessedLineItemEntityData = (
        lineItemEntityData: MasterLineItem
    ): MasterLineItem => {
        let lineItem = lineItemEntityData;

        const firstPart = lineItem.get('parts');
        const otherParts = lineItem.get('otherParts') || [];
        const totalParts = Immutable.List([firstPart, ...otherParts]);

        const carton = lineItem.get('carton');
        const cartonObject = carton?.toJS(); // Deep Empty works on JS object
        if (cartonObject && DataUtils.isDeepEmpty(cartonObject)) {
            lineItem = lineItem.delete('carton');
        }
        return lineItem.delete('otherParts').set('parts', totalParts);
    };

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }: {
        uploadedTable: Object,
        clientId: string,
        getDataForCell: () => Object
    }): Immutable.List<MasterLineItem> => {
        const lineItemsData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: LineItemImportHelper.processEntityRows
        });
        const lineItemEntities = Immutable.List(
            lineItemsData.map(lineItemEntity => {
                const processedLineItemEntity = LineItemImportHelper.getProcessedLineItemEntityData(
                    lineItemEntity
                );
                return MasterLineItem.fromJS(processedLineItemEntity.toJS());
            })
        );

        return lineItemEntities;
    };

    static hasValidationErrors = ({ entity, unitConverter }): boolean => {
        return entity.hasValidationErrors({ helpers: { unitConverter } });
    };

    static generateEntityList = (
        lineItemsData: Array<*>
    ): Immutable.List<MasterLineItem> =>
        Immutable.List(
            lineItemsData.map(lineItemEntity => {
                return MasterLineItem.fromJS(lineItemEntity);
            })
        );

    static getCustomFields(entitySettings) {
        return entitySettings?.lineItemSettings?.customFields;
    }

    static serializeBoxQuanity(
        boxQuanity: ?number,
        { entity }: SerializeEntityParamsType
    ) {
        if (DataUtils.isEmpty(entity.carton)) {
            const updatedEntity = entity.setCarton(
                Carton.fromJS({
                    capacity: boxQuanity,
                    volume: null,
                    weight: null,
                    dimensions: { length: '', breadth: '', height: '' }
                })
            );
            return updatedEntity.carton;
        }
        return entity.carton.setCapacity(boxQuanity).defaultSanitize();
    }

    static serializeCartonLength(
        length: ?string,
        { entity }: SerializeEntityParamsType
    ) {
        if (DataUtils.isEmpty(entity.carton)) {
            const updatedEntity = entity.setCarton(
                Carton.fromJS({
                    capacity: null,
                    volume: null,
                    weight: null,
                    dimensions: { length, breadth: '', height: '' }
                })
            );
            return updatedEntity.carton;
        }
        if (DataUtils.isEmpty(entity.carton?.dimensions)) {
            return entity.carton
                .setDimensions(
                    Dimensions.fromJS({ length, breadth: '', height: '' })
                )
                .defaultSanitize();
        }
        return entity.carton.dimensions.setLength(length);
    }
    static serializeCartonBredth(
        breadth: ?string,
        { entity }: SerializeEntityParamsType
    ) {
        if (DataUtils.isEmpty(entity.carton)) {
            const updatedEntity = entity.setCarton(
                Carton.fromJS({
                    capacity: null,
                    volume: null,
                    weight: null,
                    dimensions: { length: '', breadth, height: '' }
                })
            );
            return updatedEntity.carton;
        }
        if (DataUtils.isEmpty(entity.carton?.dimensions)) {
            return entity.carton
                .setDimensions(
                    Dimensions.fromJS({ length: '', breadth, height: '' })
                )
                .defaultSanitize();
        }
        return entity.carton.dimensions.setBreadth(breadth);
    }
    static serializeCartonHeight(
        height: ?string,
        { entity }: SerializeEntityParamsType
    ) {
        if (DataUtils.isEmpty(entity.carton)) {
            const updatedEntity = entity.setCarton(
                Carton.fromJS({
                    capacity: null,
                    volume: null,
                    weight: null,
                    dimensions: { length: '', breadth: '', height }
                })
            );
            return updatedEntity.carton;
        }
        if (DataUtils.isEmpty(entity.carton?.dimensions)) {
            return entity.carton
                .setDimensions(
                    Dimensions.fromJS({ length: '', breadth: '', height })
                )
                .defaultSanitize();
        }
        return entity.carton.dimensions.setHeight(height);
    }

    static processCartonVolume({
        cellValue
    }: {
        cellValue: ?string
    }): ?VolumeModelType {
        if (cellValue) {
            return { value: cellValue, unit: VolumeUnitType.MetersCubic };
        }
        return null;
    }

    static serializeCartonVolume(
        volume: VolumeModelType,
        { entity }: SerializeEntityParamsType
    ) {
        if (DataUtils.isEmpty(entity.carton)) {
            const updatedEntity = entity.setCarton(
                Carton.fromJS({
                    capacity: null,
                    volume,
                    weight: null,
                    dimensions: { length: '', breadth: '', height: '' }
                })
            );
            return updatedEntity.carton;
        }
        return entity.carton.setVolume(Volume.fromJS(volume)).defaultSanitize();
    }

    static serializeCartonWeight(
        weight: ?null,
        { entity }: SerializeEntityParamsType
    ) {
        if (DataUtils.isEmpty(entity.carton)) {
            const updatedEntity = entity.setCarton(
                Carton.fromJS({
                    capacity: null,
                    volume: null,
                    weight,
                    dimensions: { length: '', breadth: '', height: '' }
                })
            );
            return updatedEntity.carton;
        }
        return entity.carton.setWeight(weight).defaultSanitize();
    }

    static getCartonAccessor = () => ['carton'];
    static getCartonDimensionsAccessor = () => ['carton', 'dimensions'];
}

export default LineItemImportHelper;
